import { useState } from "react";
import { AlertCircle, Check, Clock } from "lucide-react";
import { getProviderConfig } from "@/pages/internal/implementation/integrationProviderConfig";
import { cn } from "@/utils/cn";
import { CardFooter, PrimaryButton, WizardCard } from "./visuals";

/**
 * Step 2: the systems the agent will use, as on the integrations page. Each
 * system appears once. Connecting opens that system's own dialog right here.
 *
 * Items that need the WhatsApp are not blocked: they wait, and connecting the
 * WhatsApp once releases all of them.
 */

const LEGAL_VISIBLE = 3;
/** Connectors whose only asset is the wide wordmark: show its symbol, on the left. */
const WORDMARK_LOGOS = new Set(["controljus"]);

function ConnectorIcon({ id, label }) {
  const icon = getProviderConfig(id)?.icon;
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-black/[.06] bg-white dark:border-white/10">
      {icon ? (
        <img src={icon} alt="" className={cn("h-6 w-6", WORDMARK_LOGOS.has(id) ? "object-cover object-left" : "object-contain")} />
      ) : (
        <span aria-hidden="true" className="text-sm font-bold text-foreground">
          {label.charAt(0)}
        </span>
      )}
    </span>
  );
}

function SystemCard({ system, waitingCount, onConnect }) {
  const connected = system.status === "connected";
  const attention = system.status === "attention";
  const isWhatsapp = system.id === "whatsapp";
  const waiting = isWhatsapp ? !connected && waitingCount > 0 : system.waitingForWhatsapp;

  return (
    <WizardCard as="article" selected={connected} aria-label={system.label}>
      <div className="flex items-center gap-3 px-5 pt-5">
        <ConnectorIcon id={system.id} label={system.label} />
        <h3 className="flex-1 text-base font-semibold text-foreground">{system.label}</h3>
        {connected ? (
          <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 dark:text-emerald-400">
            <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            Conectado
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onConnect(system.id)}
            className="inline-flex min-h-[36px] items-center rounded-full bg-foreground px-4 text-[13px] font-semibold text-background"
            aria-label={`${attention ? "Revisar" : "Conectar"} ${system.label}`}
          >
            {attention ? "Revisar" : "Conectar"}
          </button>
        )}
      </div>
      <p className="px-5 pb-4 pt-2.5 text-sm text-muted-foreground">
        {attention ? (
          <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            Precisa de atenção
          </span>
        ) : isWhatsapp && !connected ? (
          "Necessário para o agente atender."
        ) : connected && system.waitingForWhatsapp ? (
          "Conectado. Começa junto com o WhatsApp."
        ) : connected ? (
          "Pronto para o agente usar."
        ) : (
          "Opcional."
        )}
      </p>
      {waiting ? (
        <CardFooter tone="waiting">
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          {isWhatsapp
            ? `${waitingCount} ${waitingCount === 1 ? "item esperando" : "itens esperando"} a conexão`
            : "Liga com o WhatsApp"}
        </CardFooter>
      ) : (
        system.unlocks && <CardFooter>{system.unlocks}</CardFooter>
      )}
    </WizardCard>
  );
}

export default function StepSystems({ groups, waitingCount, onConnect, onBack, onNext }) {
  const [showAllLegal, setShowAllLegal] = useState(false);

  return (
    <>
      <div className="space-y-8">
        {groups.map((group) => {
          let systems = group.systems;
          let hidden = [];
          if (group.id === "legal" && !showAllLegal) {
            // The connected one first, then the most used, three at a time.
            const sorted = [...systems].sort((a, b) => (a.status === "not_connected") - (b.status === "not_connected"));
            systems = sorted.slice(0, LEGAL_VISIBLE);
            hidden = sorted.slice(LEGAL_VISIBLE);
          }
          return (
            <section key={group.id} aria-labelledby={`group-${group.id}`}>
              <h2 id={`group-${group.id}`} className="mb-3 text-[15px] font-semibold text-muted-foreground">
                {group.label}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {systems.map((system) => (
                  <SystemCard key={system.id} system={system} waitingCount={waitingCount} onConnect={onConnect} />
                ))}
              </div>
              {hidden.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllLegal(true)}
                  className="mt-2 inline-flex min-h-[44px] items-center text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  Ver outros sistemas ({hidden.map((s) => s.label).join(", ")})
                </button>
              )}
            </section>
          );
        })}
      </div>

      <div className="mt-9 flex flex-wrap items-center justify-end gap-5">
        <button type="button" onClick={onBack} className="min-h-[44px] text-[15px] text-muted-foreground hover:text-foreground">
          Voltar
        </button>
        <PrimaryButton onClick={onNext}>Continuar</PrimaryButton>
      </div>
    </>
  );
}
